import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  fetchEventActions,
  fetchEventJobs,
  fetchEventJobStats,
  fetchEventJobTimeseries,
  fetchEventJobUsers,
  publishEvent,
  type EventActionsFilters,
  type EventActionsPage,
  type EventJobsFilters,
  type EventJobsPage,
  type EventJobTimeseriesFilters,
  type PublishEventInput,
} from "@/features/events/api/events.api";

/**
 * KPI cards cover a rolling window, so they go stale quickly but not per
 * render — the list's own polling already drives the sense of liveness.
 */
export const EVENTS_STATS_STALE_TIME_MS = 30 * 1000;
const EVENTS_USERS_STALE_TIME_MS = 5 * 60 * 1000;

/** History is only consulted to suggest a payload shape, which barely changes. */
const EVENTS_LAST_PAYLOAD_STALE_TIME_MS = 5 * 60 * 1000;

export const eventsKeys = {
  all: ["events"] as const,
  list: (filters: EventJobsFilters) => ["events", "list", filters] as const,
  actions: (filters: EventActionsFilters) => ["events", "actions", filters] as const,
  users: ["events", "users"] as const,
  stats: (windowHours?: number) => ["events", "stats", windowHours ?? null] as const,
  timeseries: (filters: EventJobTimeseriesFilters) =>
    ["events", "timeseries", filters] as const,
  lastPayload: (eventType: string) => ["events", "last-payload", eventType] as const,
} as const;

/**
 * The payload of the most recent event of one type. Events published from
 * scratch have no schema behind them, so the publish sheet offers this as the
 * only honest hint about which fields a consumer expects.
 */
export function lastEventPayloadQueryOptions(eventType: string) {
  return queryOptions({
    queryKey: eventsKeys.lastPayload(eventType),
    queryFn: async () => {
      const page = await fetchEventJobs({
        eventTypeIn: [eventType],
        limit: 1,
        sortBy: "publishedAt",
        sortOrder: "desc",
      });
      return page.items[0]?.payload ?? null;
    },
    enabled: eventType.length > 0,
    staleTime: EVENTS_LAST_PAYLOAD_STALE_TIME_MS,
    meta: { skipGlobalError: true },
  });
}

export function eventsUsersQueryOptions() {
  return queryOptions({
    queryKey: eventsKeys.users,
    queryFn: fetchEventJobUsers,
    staleTime: EVENTS_USERS_STALE_TIME_MS,
  });
}

/**
 * `keepPreviousData` holds the last window's numbers while the next one is in
 * flight, so changing the range moves the strip's values rather than tearing it
 * down to skeletons. The caller is expected to mark the held-over numbers as
 * provisional — `isPlaceholderData` says when they belong to the old window.
 */
export function eventsStatsQueryOptions(windowHours?: number) {
  return queryOptions({
    queryKey: eventsKeys.stats(windowHours),
    queryFn: () => fetchEventJobStats(windowHours),
    staleTime: EVENTS_STATS_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * Bucketed volume across the same window the KPI strip reports, so the two
 * agree. `keepPreviousData` holds the last shape while a filter change is in
 * flight — an empty axis flashing between two populated ones reads as an outage.
 */
export function eventsTimeseriesQueryOptions(filters: EventJobTimeseriesFilters) {
  return queryOptions({
    queryKey: eventsKeys.timeseries(filters),
    queryFn: () => fetchEventJobTimeseries(filters),
    staleTime: EVENTS_STATS_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * The published event lands as a PENDING row, so the list and the KPI counts
 * are both stale the moment this resolves.
 */
export function usePublishEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PublishEventInput) => publishEvent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    },
    meta: { skipGlobalError: true },
  });
}

/** One correlation's fan-out; well under the API max of 200. */
export const EVENTS_BY_CORRELATION_LIMIT = 100;

/**
 * The events that make up one action, in publish order. The drawer uses this
 * rather than jumping the table onto a search, so the action stays in view
 * while its fan-out is read.
 */
export function eventsByCorrelationQueryOptions(
  correlationId: string,
  windowHours?: number,
) {
  const filters: EventJobsFilters = {
    correlationId,
    hidePings: false,
    windowHours,
    limit: EVENTS_BY_CORRELATION_LIMIT,
    sortBy: "publishedAt",
    sortOrder: "asc",
  };
  return queryOptions({
    queryKey: eventsKeys.list(filters),
    queryFn: () => fetchEventJobs(filters),
    enabled: correlationId.length > 0,
  });
}

export function eventsInfiniteQueryOptions(filters: EventJobsFilters) {
  return infiniteQueryOptions({
    queryKey: eventsKeys.list(filters),
    queryFn: ({ pageParam }) => fetchEventJobs(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: EventJobsPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

/**
 * The same window, grouped into the user actions that produced it. Paginated
 * over its own cursor rather than the event list's, because a page of actions
 * is a page of correlations and not a page of rows.
 */
export function eventActionsInfiniteQueryOptions(filters: EventActionsFilters) {
  return infiniteQueryOptions({
    queryKey: eventsKeys.actions(filters),
    queryFn: ({ pageParam }) => fetchEventActions(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: EventActionsPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}
